
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import { IDrawableItemEditable } from "../../../IDrawableItemEditable";
import { ClaioViewerLauncher, IClaioImageSource } from "../../../../../../domain/claio";
import { Patient } from "../../../../../../domain/patient";
import { Department } from "../../../../../../domain/department";
import ClaioImageSizeSelector from "../claio/ClaioImageSizeSelector.vue";
import { Size } from "../../../../../../domain/drawing";

@Component
export default class ClaioContentsToolMixin extends Mixins() {
  data(): any {
    return {
      currentPage: 1,
      foundClaioImages: [] as IClaioImageSource[],
      displayClaioImages: [] as IClaioImageSource[],
      isClaioImageFinding: false,
      isFitted: true,
      thumbnailSize: ClaioImageSizeSelector.Option.small,
      selectedClaioImageSource: null as IClaioImageSource,
      enableLayer: true,
      selectedThumbnailIndex: null,
    };
  }
  currentPage: number;
  foundClaioImages: IClaioImageSource[];
  displayClaioImages: IClaioImageSource[];
  isClaioImageFinding: boolean;
  isFitted: boolean;
  thumbnailSize: Size;
  selectedClaioImageSource: IClaioImageSource;
  enableLayer: boolean;
  selectedThumbnailIndex: number;

  // 最後に選択した画像エリアにClaio画像を貼り付ける用途がある場合は使用する
  // その用途が無い場合は不要
  @Prop()
  source: IDrawableItemEditable;

  @Prop()
  patient: Patient;

  @Prop()
  department: Department;

  @Prop({ default: true })
  isPastable: boolean;

  @Prop()
  orderNo: string;

  mounted(): void {
    this.isFitted = !this.shouldDisplayActualSize;
  }

  get previewStyle() {
    return {
      height: (this.$configuration.behavior.document.editor.contents.claio.preview.height - 10) + "px", // 高さがpaddingの分ずれるので調整.
    };
  }

  get imageCountPerPage(): number {
    return this.$configuration.behavior.document.editor.contents.claio.displayCountPerPage;
  }

  get shouldDisplayActualSize(): boolean {
    return this.$configuration.behavior.document.editor.contents.claio.preview.shouldDisplayActualSize;
  }

  thumbnailSelected(src: IClaioImageSource, index: number): void {
    this.selectedClaioImageSource = src;
    this.selectedThumbnailIndex = index;
    this.selected(this.selectedClaioImageSource);
  }

  launchClaio(src: IClaioImageSource): void {
    const launcher = new ClaioViewerLauncher(this.$configuration);
    const departmentId = this.department ? this.department.id : null;
    launcher.launch(this.$store.getters.user, this.patient, src.claioImageKey, src.departmentId, src.isMovie, departmentId)
    .then(() => {
      // DO NOTHING
    })
    .catch(e => {
      this.$error("Claio画像ビューアー起動失敗", "Claio画像ビューアーの起動処理中にエラーが発生しました。", e);
    });
  }

  resetScrollPosition(): void {
    // ページ遷移が起こった時にスクロールを初期位置に戻す
    const container = this.$refs.container as HTMLDivElement;
    if (!container) { return; }
    container.scrollTop = 0;
    container.scrollLeft = 0;
  }

  @Watch("enableLayer")
  onEnableLayerUpdated(newValue: boolean, oldValue: boolean): void {
    this.setLayerVisibility(newValue);
  }

  setLayerVisibility(value: boolean): void {
    if (!this.foundClaioImages) { return; }
    for (let index = 0; index < this.foundClaioImages.length; ++index) {
      if (this.foundClaioImages[index].isVisibleLayer === value) { continue; }
      const cloned = this.foundClaioImages[index].clone();
      cloned.isVisibleLayer = value;
      this.foundClaioImages.splice(index, 1, cloned);
    }
  }

  @Watch("foundClaioImages")
  onFoundClaioImagesUpdated(): void {
    for (const image of this.foundClaioImages) {
      image.isVisibleLayer = this.enableLayer;
    }
    this.updateDisplayClaioImages();
    this.updateImageSelection();
  }

  @Watch("currentPage")
  onCurrentPageUpdated(): void {
    this.updateDisplayClaioImages();
    this.updateImageSelection();
  }

  updateDisplayClaioImages(): void {
    this.displayClaioImages = !!this.foundClaioImages
      ? this.foundClaioImages.slice(this.imageCountPerPage * (this.currentPage - 1), this.imageCountPerPage * this.currentPage)
      : [];
  }
  
  updateImageSelection() {
    this.selectedThumbnailIndex = null;
    if (this.selectedClaioImageSource) {
      const found = this.foundClaioImages.find(i => i.claioImageKey === this.selectedClaioImageSource.claioImageKey);
      this.selectedClaioImageSource = !!found ? found : null;
      if (!!found) {
        this.selectedThumbnailIndex = this.displayClaioImages.findIndex(i => i.claioImageKey === this.selectedClaioImageSource.claioImageKey);
      }
      this.selected(this.selectedClaioImageSource);
    }
  }

  @Emit()
  selected(src: IClaioImageSource): void {}

  @Emit()
  imageLoaded(): void {}

  isImageChangeable(shiftValue: number): boolean {
    return !!this.getChangeDestinationImageSource(shiftValue);
  }

  private getChangeDestinationImageSource(shiftValue: number): IClaioImageSource {
    if (!this.selectedClaioImageSource) { return null; }
    const index = this.foundClaioImages.findIndex(i => i.claioImageKey === this.selectedClaioImageSource.claioImageKey);
    const destinationIndex = index + shiftValue;
    return ((destinationIndex >= 0) && (destinationIndex < this.foundClaioImages.length)) ? this.foundClaioImages[destinationIndex] : null;
  }

  async changeImage(shiftValue: number): Promise<void> {
    const destinationImageSource = this.getChangeDestinationImageSource(shiftValue);
    if (!destinationImageSource) { return; }
    if (!this.displayClaioImages.some(i => i.claioImageKey === destinationImageSource.claioImageKey)) {
      // ページ切り替えが必要
      const destinationIndex = this.foundClaioImages.findIndex(i => i.claioImageKey === destinationImageSource.claioImageKey);
      const nextPage = Math.floor(destinationIndex / this.imageCountPerPage) + 1;
      const maxPage = Math.ceil(this.foundClaioImages.length / this.imageCountPerPage);
      if (nextPage >= 1 && nextPage <= maxPage) {
        this.currentPage = nextPage;
        await this.$nextTick();
      }
    }
    const thumbnailIndex = this.displayClaioImages.findIndex(i => i.claioImageKey === destinationImageSource.claioImageKey);
    if (thumbnailIndex < 0) { return; }
    this.thumbnailSelected(destinationImageSource, thumbnailIndex);
  }
}
